/**
 * The clipboard, in one function.
 *
 * `integrations/clock.ts` and `integrations/navigate.ts`'s neighbour, for their reason:
 * a browser global a page reaches for directly is a global three pages reach for three
 * ways. It was written first for delegated access (`pages/client/files/DelegatedAccess.tsx`)
 * and session 46's Connect page needed it four more times — a publishable key, a signing
 * secret shown once, and every example request — so it moved here rather than being
 * written a second time.
 *
 * **`navigator.clipboard.writeText` and nothing else.** No `document.execCommand`
 * fallback: that one needs a node holding the text and a selection over it, which for a
 * secret is more of the secret in more of the page than the one element it is already
 * in. Where the API is missing — jsdom, plain HTTP, a few embedded browsers — the answer
 * is `'no'` and the caller says so and leaves the text to be selected by hand.
 *
 * **The rejection is not passed on.** A `DOMException` from a clipboard write can carry
 * the text it was asked to write, and that text may be a customer's password or a
 * signing secret; so it is discarded rather than reported, here and by every caller.
 */

/** Whether a copy reached the clipboard. */
export type CopyState = 'yes' | 'no'

/** Put `value` on the clipboard, and say whether it got there. */
export function copyToClipboard(value: string, said: (state: CopyState) => void): void {
  // The DOM types promise a `Clipboard`; the platform does not. It is absent in jsdom,
  // absent over plain HTTP, and absent in a few embedded browsers — so it is read as a
  // value that may not be there rather than trusted from its declaration.
  const clipboard = (navigator as { clipboard?: Clipboard }).clipboard
  if (clipboard === undefined) {
    said('no')
    return
  }
  void clipboard.writeText(value).then(
    () => {
      said('yes')
    },
    () => {
      said('no')
    },
  )
}
